import { Component, inject, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, PopupRequest } from '@azure/msal-browser';
import { Subject, takeUntil } from 'rxjs';
import { setAuthToken } from '../../../app/state/actions';
import { Store } from '@ngrx/store';
/** This component is used for re-authorizing the token */
@Component({
  selector: 'app-token-dialog',
  templateUrl: './token-dialog.component.html',
  styleUrl: './token-dialog.component.scss'
})
export class TokenDialogComponent implements OnDestroy {

  readonly dialogRef = inject(MatDialogRef<TokenDialogComponent>); /** Reference to the dialog instance for closing it programmatically */
  private readonly destroy$ = new Subject(); /** Subject used to unsubscribe from observables to prevent memory leaks */

  /** True while the Active Directory popup is open, so the button cannot be clicked twice. */
  isAuthorizing = false;

  /** Shown when a sign-in attempt fails; the dialog cannot be dismissed, so it has to offer a retry. */
  errorMessage = '';

  /**
   *
   * @param msalGuardConfig Inject MSAL configuration for guard-based login
   * @param authService MSAL service to handle login
   * @param store NgRx store to dispatch auth actions
   */
  constructor(
    @Inject(MSAL_GUARD_CONFIG) public msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private store: Store,) {

  }
  /**
   * Initiates the MSAL login process using a popup.
   * If a specific auth request is configured, it's used; otherwise, the default loginPopup is triggered.
   * After login:
   * - The active MSAL account is set.
   * - The authentication token is retrieved from local storage and dispatched to the store.
   * - The dialog is closed.
   */
  login() {
    this.store.dispatch(setAuthToken({ authToken: '' }));
    this.isAuthorizing = true;
    this.errorMessage = '';

    const authRequest = this.msalGuardConfig.authRequest
      ? { ...this.msalGuardConfig.authRequest } as PopupRequest
      : undefined;

    const login$ = authRequest
      ? this.authService.loginPopup(authRequest)
      : this.authService.loginPopup();

    login$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);

          // Store and dispatch the NEW token AFTER login completes
          localStorage.setItem('token', response.accessToken);
          this.store.dispatch(setAuthToken({ authToken: response.accessToken }));
          this.isAuthorizing = false;
          this.dialogRef.close();
        },
        error: () => {
          // A cancelled or blocked popup must leave the dialog usable — it cannot be
          // dismissed, so without a retry the session would be stuck here.
          this.isAuthorizing = false;
          this.errorMessage = 'Sign-in did not complete. Please try again.';
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
